import { CalendarDays, List, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ContentCalendar, {
  ContentCalendarView,
} from "../components/ContentCalendar";
import ContentList, { ContentListFilter } from "../components/ContentList";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { getPostsView, postsSearchForView, PostsView } from "../lib/postsView";

const viewOptions: Array<{
  value: PostsView;
  label: string;
  icon: typeof List;
}> = [
  { value: "calendar", label: "Calendar", icon: CalendarDays },
  { value: "list", label: "List", icon: List },
];

export default function PostsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = getPostsView(searchParams);
  const [calendarDate, setCalendarDate] = useState<Date>();
  const [calendarView, setCalendarView] = useState<ContentCalendarView>();
  const [listFilter, setListFilter] = useState<ContentListFilter>();
  const [listLimitCount, setListLimitCount] = useState<number>();

  const changeView = (view: PostsView) => {
    setSearchParams(
      new URLSearchParams(postsSearchForView(searchParams, view)),
      { replace: true },
    );
  };

  return (
    <section className="p-4 md:px-6 md:py-5" aria-labelledby="posts-page-title">
      <header className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1
            id="posts-page-title"
            className="text-2xl font-semibold tracking-tight text-foreground"
          >
            Posts
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Scheduled and published content across your channels.
          </p>
        </div>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 lg:flex lg:w-auto">
          <div
            className="inline-flex min-w-0 rounded-lg bg-muted p-1"
            role="group"
            aria-label="Post view"
          >
            {viewOptions.map((option) => {
              const isActive = activeView === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => changeView(option.value)}
                  className={cn(
                    "inline-flex min-h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-2 text-sm font-medium transition-colors sm:px-3 lg:flex-none",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    isActive
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <option.icon className="h-4 w-4" aria-hidden="true" />
                  {option.label}
                </button>
              );
            })}
          </div>

          <Button
            className="min-h-11 shrink-0"
            onClick={() => navigate("/generate")}
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            <span className="ml-1.5">Generate</span>
          </Button>
        </div>
      </header>

      <div className="min-w-0">
        {activeView === "calendar" ? (
          <ContentCalendar
            embedded
            selectedDate={calendarDate}
            calendarView={calendarView}
            onSelectedDateChange={setCalendarDate}
            onCalendarViewChange={setCalendarView}
          />
        ) : (
          <ContentList
            embedded
            filter={listFilter}
            limitCount={listLimitCount}
            onFilterChange={setListFilter}
            onLimitCountChange={setListLimitCount}
          />
        )}
      </div>
    </section>
  );
}
